/**
 * Utilidades para manejo de tokens de DataFast (Pagos Recurrentes)
 */

/**
 * Valida los datos de un token antes de procesarlo
 * @param {Object} tokenData - Datos del token
 * @returns {Array} Array de errores (vacío si es válido)
 */
export function validateTokenData(tokenData) {
    const errors = []

    if (!tokenData || typeof tokenData !== 'object') {
        errors.push('Datos de token inválidos')
        return errors
    }

    if (!tokenData.token || typeof tokenData.token !== 'string') {
        errors.push('Token es requerido')
    }

    if (!tokenData.idusuario) {
        errors.push('ID de usuario es requerido')
    }

    if (!tokenData.idcontrato) {
        errors.push('ID de contrato es requerido')
    }

    return errors
}

/**
 * Formatea los datos de tarjeta para mostrar
 * @param {Object} cardData - Datos de la tarjeta
 * @returns {Object} Datos formateados
 */
export function formatCardData(cardData) {
    return {
        id: cardData.id || null,
        token: cardData.token || cardData.registrationId || '',
        brand: cardData.brand || cardData.cardBrand || 'CARD',
        lastDigits: cardData.lastDigits || cardData.last4 || '****',
        holder: cardData.holder || cardData.cardHolder || '',
        expiryMonth: cardData.expiryMonth || cardData.expiry_month || '**',
        expiryYear: cardData.expiryYear || cardData.expiry_year || '****',
        isActive: cardData.isActive || cardData.is_active || false,
        createdAt: cardData.createdAt || cardData.created_at || null,
        dia: cardData.dia || cardData.billing_day || null,
        idcontrato: cardData.idcontrato || null,
    }
}

/**
 * Obtiene el nombre de la marca de tarjeta
 * @param {String} brand - Código de marca
 * @returns {String} Nombre legible de la marca
 */
export function getCardBrandName(brand) {
    const brands = {
        VISA: 'Visa',
        MASTER: 'Mastercard',
        MASTERCARD: 'Mastercard',
        AMEX: 'American Express',
        DINERS: 'Diners Club',
        DISCOVER: 'Discover',
    }

    return brands[brand?.toUpperCase()] || brand || 'Tarjeta'
}

/**
 * Formatea número de tarjeta para mostrar
 * @param {String} lastDigits - Últimos dígitos
 * @returns {String} Número formateado
 */
export function formatCardNumber(lastDigits) {
    return `•••• •••• •••• ${lastDigits}`
}

/**
 * Valida fecha de expiración de tarjeta
 * @param {String|Number} month - Mes (1-12)
 * @param {String|Number} year - Año (YYYY o YY)
 * @returns {Boolean} true si la tarjeta no ha expirado
 */
export function isCardValid(month, year) {
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth() + 1

    let expYear = parseInt(year, 10)
    const expMonth = parseInt(month, 10)

    // Si el año es de 2 dígitos, convertir a 4 dígitos
    if (expYear < 100) {
        expYear += 2000
    }

    // Verificar si ha expirado
    if (expYear < currentYear) {
        return false
    }

    if (expYear === currentYear && expMonth < currentMonth) {
        return false
    }

    return true
}

/**
 * Calcula la próxima fecha de cobro para suscripciones
 * @param {String} frequency - Frecuencia (monthly, weekly, yearly)
 * @param {Date} lastChargeDate - Última fecha de cobro
 * @returns {Date} Próxima fecha de cobro
 */
export function calculateNextChargeDate(
    frequency = 'monthly',
    lastChargeDate = null
) {
    const baseDate = lastChargeDate ? new Date(lastChargeDate) : new Date()

    switch (frequency.toLowerCase()) {
        case 'weekly':
            baseDate.setDate(baseDate.getDate() + 7)
            break
        case 'monthly':
            baseDate.setMonth(baseDate.getMonth() + 1)
            break
        case 'quarterly':
            baseDate.setMonth(baseDate.getMonth() + 3)
            break
        case 'yearly':
            baseDate.setFullYear(baseDate.getFullYear() + 1)
            break
        default:
            baseDate.setMonth(baseDate.getMonth() + 1)
    }

    return baseDate
}

/**
 * Formatea fecha de cobro para mostrar
 * @param {Date|String} date - Fecha
 * @returns {String} Fecha formateada
 */
export function formatChargeDate(date) {
    const d = date instanceof Date ? date : new Date(date)
    return d.toLocaleDateString('es-EC', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    })
}

/**
 * Parsea respuesta de tokenización de DataFast
 * @param {Object} response - Respuesta del servidor
 * @returns {Object} Datos parseados
 */
export function parseTokenizationResponse(response) {
    const data = response?.data?.data || response?.data || {}

    return {
        success: data.success || data.status === 'approved' || false,
        token: data.token || data.registrationId || data.id || null,
        cardBrand: data.cardBrand || data.brand || data.paymentBrand || null,
        lastDigits: data.lastDigits || data.last4Digits || null,
        expiryMonth: data.expiryMonth || data.card?.expiryMonth || null,
        expiryYear: data.expiryYear || data.card?.expiryYear || null,
        holder: data.holder || data.card?.holder || null,
        message: data.message || 'Tokenización procesada',
        code: data.code || data.resultCode || null,
    }
}

/**
 * Valida si un pago puede ser recurrente
 * @param {Object} paymentData - Datos del pago
 * @returns {Object} { valid: boolean, errors: Array }
 */
export function validateRecurringPayment(paymentData) {
    const errors = []

    if (!paymentData.amount || paymentData.amount <= 0) {
        errors.push('El monto debe ser mayor a 0')
    }

    if (!paymentData.idusuario) {
        errors.push('ID de usuario es requerido')
    }

    if (!paymentData.idcontrato) {
        errors.push('ID de contrato es requerido')
    }

    // Validar que el monto no sea extremadamente alto
    if (paymentData.amount > 10000) {
        errors.push('El monto para pagos recurrentes no puede exceder $10,000')
    }

    return {
        valid: errors.length === 0,
        errors,
    }
}

/**
 * Sanitiza datos de tarjeta para almacenamiento
 * @param {Object} cardData - Datos de tarjeta
 * @returns {Object} Datos sanitizados
 */
export function sanitizeCardData(cardData) {
    return {
        token: cardData.token || '',
        brand: (cardData.brand || '').toUpperCase(),
        lastDigits: (cardData.lastDigits || '').slice(-4),
        holder: (cardData.holder || '').toUpperCase().slice(0, 100),
        expiryMonth: String(cardData.expiryMonth).padStart(2, '0'),
        expiryYear: String(cardData.expiryYear),
        isActive: Boolean(cardData.isActive),
    }
}

/**
 * Genera mensaje de error amigable para códigos de error comunes
 * @param {String} errorCode - Código de error
 * @returns {String} Mensaje amigable
 */
export function getFriendlyErrorMessage(errorCode) {
    const errorMessages = {
        '000.100.110': 'Transacción rechazada - Fondos insuficientes',
        '000.100.111': 'Tarjeta inválida o expirada',
        '000.100.112': 'Transacción rechazada - Tarjeta bloqueada',
        '800.100.151': 'Transacción pendiente',
        '800.100.152': 'Transacción en proceso',
        '000.400.000': 'Transacción aprobada exitosamente',
        '000.000.000': 'Transacción exitosa',
        '100.100.303': 'Tokenización rechazada',
        '800.120.100': 'Tokenización rechazada - Datos inválidos',
        '200.300.404':
            'Checkout inválido o expirado. Genera un nuevo pago y vuelve a intentarlo.',
    }

    return errorMessages[errorCode] || `Error: ${errorCode}`
}

/**
 * Verifica si un código de respuesta indica éxito
 * @param {String} code - Código de respuesta
 * @returns {Boolean} true si es exitoso
 */
export function isSuccessCode(code) {
    const successCodes = [
        '000.000.000',
        '000.100.110',
        '000.100.112',
        '000.400.000',
        '000.400.010',
        '000.400.020',
    ]

    // Los códigos que empiezan con 000.000 o 000.100.1 generalmente son exitosos
    return (
        successCodes.includes(code) ||
        /^000\.000/.test(code) ||
        /^000\.100\.1/.test(code)
    )
}

export default {
    validateTokenData,
    formatCardData,
    getCardBrandName,
    formatCardNumber,
    isCardValid,
    calculateNextChargeDate,
    formatChargeDate,
    parseTokenizationResponse,
    validateRecurringPayment,
    sanitizeCardData,
    getFriendlyErrorMessage,
    isSuccessCode,
}
